import service from '../../utils/request';
import type {
  ShiftSetView,
  ShiftSetInput,
  RecurringShiftRuleView,
  RecurringShiftRuleInput,
  ScheduledShiftView,
  ScheduledShiftInput,
} from '../../types/scheduling';

type ApiResult<T> = { payload: T; status: number; errors?: Array<any> };

// Helper to unwrap standard Result<T> envelope
function unwrapResult<T>(data: any): T {
  if (data && typeof data === 'object' && 'payload' in data && 'status' in data) {
    return (data as ApiResult<T>).payload;
  }
  return data as T;
}

// ============================================================================
// Shift Sets API
// ============================================================================

/**
 * Fetch all shift sets (schedule templates) for the current user's company
 */
export async function listShiftSets(): Promise<ShiftSetView[]> {
  const response = await service.get('/ShiftSets');
  return unwrapResult<ShiftSetView[]>(response.data);
}

/**
 * Get a specific shift set by ID
 */
export async function getShiftSet(id: number): Promise<ShiftSetView> {
  const response = await service.get(`/ShiftSets/${id}`);
  return unwrapResult<ShiftSetView>(response.data);
}

/**
 * Create a new shift set
 */
export async function createShiftSet(input: ShiftSetInput): Promise<ShiftSetView> {
  const response = await service.post('/ShiftSets', input);
  return unwrapResult<ShiftSetView>(response.data);
}

/**
 * Update an existing shift set
 */
export async function updateShiftSet(id: number, input: ShiftSetInput): Promise<ShiftSetView> {
  const response = await service.put(`/ShiftSets/${id}`, input);
  return unwrapResult<ShiftSetView>(response.data);
}

/**
 * Delete (archive) a shift set
 */
export async function deleteShiftSet(id: number, options?: { cascade?: boolean }): Promise<boolean> {
  const response = await service.delete(`/ShiftSets/${id}`, {
    params: options?.cascade ? { cascade: true } : undefined,
  });
  return unwrapResult<boolean>(response.data);
}

/**
 * Change a schedule's name and nothing else: no rule, shift, employee or notification (CO-1021).
 */
export async function renameShiftSet(id: number, name: string): Promise<ShiftSetView> {
  const response = await service.post(`/ShiftSets/${id}/rename`, { name });
  return unwrapResult<ShiftSetView>(response.data);
}

/**
 * Assign users to a shift set
 */
export async function assignUsersToShiftSet(id: number, userIds: string[]): Promise<ShiftSetView> {
  const response = await service.post(`/ShiftSets/${id}/assign-users`, userIds);
  return unwrapResult<ShiftSetView>(response.data);
}

// ============================================================================
// Recurring Shift Rules API
// ============================================================================

/**
 * Fetch all recurring shift rules for the current user's company
 */
export async function listRecurringRules(): Promise<RecurringShiftRuleView[]> {
  const response = await service.get('/RecurringShiftRules');
  return unwrapResult<RecurringShiftRuleView[]>(response.data);
}

/**
 * Get a specific recurring rule by ID
 */
export async function getRecurringRule(id: number): Promise<RecurringShiftRuleView> {
  const response = await service.get(`/RecurringShiftRules/${id}`);
  return unwrapResult<RecurringShiftRuleView>(response.data);
}

/**
 * Create a new recurring shift rule
 */
export async function createRecurringRule(input: RecurringShiftRuleInput): Promise<RecurringShiftRuleView> {
  const response = await service.post('/RecurringShiftRules', input);
  return unwrapResult<RecurringShiftRuleView>(response.data);
}

/**
 * Update an existing recurring rule
 */
export async function updateRecurringRule(id: number, input: RecurringShiftRuleInput): Promise<RecurringShiftRuleView> {
  const response = await service.put(`/RecurringShiftRules/${id}`, input);
  return unwrapResult<RecurringShiftRuleView>(response.data);
}

/**
 * Delete (archive) a recurring rule
 */
export async function deleteRecurringRule(id: number): Promise<boolean> {
  const response = await service.delete(`/RecurringShiftRules/${id}`);
  return unwrapResult<boolean>(response.data);
}

// ============================================================================
// Scheduled Shifts API (Occurrences)
// ============================================================================

/**
 * Get scheduled shifts (materialized + virtual) for a date range
 * @param fromDate ISO date string (e.g., '2025-11-21')
 * @param toDate ISO date string (e.g., '2025-11-27')
 */
export async function getScheduledShifts(fromDate: string, toDate: string, filters?: { userId?: string; linkedRecurringRuleId?: number; shiftSetId?: number }): Promise<ScheduledShiftView[]> {
  const params = new URLSearchParams();
  if (filters?.userId) params.set('userId', filters.userId);
  if (typeof filters?.linkedRecurringRuleId === 'number') params.set('linkedRecurringRuleId', String(filters.linkedRecurringRuleId));
  if (typeof filters?.shiftSetId === 'number') params.set('shiftSetId', String(filters.shiftSetId));
  const qs = params.toString();
  const url = qs ? `/Shift/GetScheduledShifts/${fromDate}/${toDate}?${qs}` : `/Shift/GetScheduledShifts/${fromDate}/${toDate}`;
  const response = await service.get(url);
  return unwrapResult<ScheduledShiftView[]>(response.data);
}

/**
 * Create or update a single scheduled shift (manual occurrence)
 */
export async function setScheduledShift(input: ScheduledShiftInput): Promise<ScheduledShiftView> {
  // Backend expects StartTime/EndTime as ISO datetime strings. Convert minutes to HH:mm on the given date.
  const startHHmm = minutesToTime(input.startTimeMinutes);
  const endHHmm = minutesToTime(input.endTimeMinutes);
  const breakHHmm = typeof input.breakStartTimeMinutes === 'number' ? minutesToTime(input.breakStartTimeMinutes) : null;
  const breakEndHHmm = typeof input.breakEndTimeMinutes === 'number' ? minutesToTime(input.breakEndTimeMinutes) : null;

  const addDaysIsoDate = (isoDate: string, days: number): string => {
    const [y, m, d] = isoDate.split('-').map(n => Number(n));
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
  };

  // Overnight support: if end time is <= start time, treat end as next day.
  const endDate = (input.endTimeMinutes <= input.startTimeMinutes)
    ? addDaysIsoDate(input.date, 1)
    : input.date;

  const overnight = input.endTimeMinutes <= input.startTimeMinutes;
  const breakDate = (typeof input.breakStartTimeMinutes === 'number' && breakHHmm)
    ? ((overnight && input.breakStartTimeMinutes < input.startTimeMinutes) ? addDaysIsoDate(input.date, 1) : input.date)
    : null;
  const breakEndDate = (typeof input.breakEndTimeMinutes === 'number' && breakEndHHmm)
    ? ((overnight && input.breakEndTimeMinutes < input.startTimeMinutes) ? addDaysIsoDate(input.date, 1) : input.date)
    : null;

  // Server parser expects a plain local datetime string (no timezone designator)
  const startIso = `${input.date} ${startHHmm}:00`;
  const endIso = `${endDate} ${endHHmm}:00`;
  const breakStartIso = (breakDate && breakHHmm) ? `${breakDate} ${breakHHmm}:00` : undefined;
  const breakEndIso = (breakEndDate && breakEndHHmm) ? `${breakEndDate} ${breakEndHHmm}:00` : undefined;

  const serverInput = {
    id: input.id ?? 0,
    userId: input.userId,
    startTime: startIso,
    endTime: endIso,
    breakStartTime: breakStartIso,
    breakEndTime: breakEndIso,
    breakDurationMinutes: input.breakDurationMinutes ?? undefined,
    overrideReason: input.overrideReason,
  };

  const response = await service.post('/Shift/SetScheduledShift', serverInput);
  return unwrapResult<ScheduledShiftView>(response.data);
}

/**
 * Create or update an override (convert virtual to manual or edit existing)
 */
export async function createOverride(input: ScheduledShiftInput): Promise<ScheduledShiftView> {
  const startHHmm = minutesToTime(input.startTimeMinutes);
  const endHHmm = minutesToTime(input.endTimeMinutes);
  const breakHHmm = typeof input.breakStartTimeMinutes === 'number' ? minutesToTime(input.breakStartTimeMinutes) : null;
  const breakEndHHmm = typeof input.breakEndTimeMinutes === 'number' ? minutesToTime(input.breakEndTimeMinutes) : null;

  const addDaysIsoDate = (isoDate: string, days: number): string => {
    const [y, m, d] = isoDate.split('-').map(n => Number(n));
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
  };

  const endDate = (input.endTimeMinutes <= input.startTimeMinutes)
    ? addDaysIsoDate(input.date, 1)
    : input.date;

  const overnight = input.endTimeMinutes <= input.startTimeMinutes;
  const breakDate = (typeof input.breakStartTimeMinutes === 'number' && breakHHmm)
    ? ((overnight && input.breakStartTimeMinutes < input.startTimeMinutes) ? addDaysIsoDate(input.date, 1) : input.date)
    : null;
  const breakEndDate = (typeof input.breakEndTimeMinutes === 'number' && breakEndHHmm)
    ? ((overnight && input.breakEndTimeMinutes < input.startTimeMinutes) ? addDaysIsoDate(input.date, 1) : input.date)
    : null;

  const startIso = `${input.date} ${startHHmm}:00`;
  const endIso = `${endDate} ${endHHmm}:00`;
  const breakStartIso = (breakDate && breakHHmm) ? `${breakDate} ${breakHHmm}:00` : undefined;
  const breakEndIso = (breakEndDate && breakEndHHmm) ? `${breakEndDate} ${breakEndHHmm}:00` : undefined;

  const serverInput = {
    id: input.id ?? 0,
    userId: input.userId,
    startTime: startIso,
    endTime: endIso,
    breakStartTime: breakStartIso,
    breakEndTime: breakEndIso,
    breakDurationMinutes: input.breakDurationMinutes ?? undefined,
    overrideReason: input.overrideReason,
  };

  const response = await service.post('/Shift/Override', serverInput);
  return unwrapResult<ScheduledShiftView>(response.data);
}

/**
 * Delete a scheduled shift occurrence
 */
export async function deleteScheduledShift(id: number): Promise<boolean> {
  const response = await service.delete(`/Shift/${id}`);
  return unwrapResult<boolean>(response.data);
}

/**
 * Delete an override occurrence
 */
export async function deleteOverride(id: number): Promise<boolean> {
  const response = await service.delete(`/Shift/Override/${id}`);
  return unwrapResult<boolean>(response.data);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert minutes from midnight to HH:mm format
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Convert HH:mm time string to minutes from midnight
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert date to ISO date string (YYYY-MM-DD) in the local timezone.
 * Using toISOString() would convert to UTC first, which shifts the date by -1 day
 * for UTC+ timezones, causing the API to miss shifts on the last day of the view.
 */
export function dateToISOString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
